import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { Typewriter } from "react-simple-typewriter";
import "react-tooltip/dist/react-tooltip.css";
import {
  SiReact,
  SiNodedotjs,
  SiExpress,
  SiMongodb,
  SiHtml5,
  SiCss,
  SiFirebase,
  SiSqlite,
  SiPython,
  SiFlask,
  SiJavascript,
  SiGit,
  SiGithub,
  SiSalesforce,
} from "react-icons/si";

  // 🔹 Map stack name → icon
  const techIcons = {
    React: <SiReact className="text-cyan-400 w-4 h-4" />,
    "Node.js": <SiNodedotjs className="text-green-500 w-4 h-4" />,
    Express: <SiExpress className="text-gray-300 w-4 h-4" />,
    MongoDB: <SiMongodb className="text-green-400 w-4 h-4" />,
    HTML5: <SiHtml5 className="text-orange-500 w-4 h-4" />,
    CSS3: <SiCss className="text-blue-500 w-4 h-4" />,
    Firebase: <SiFirebase className="text-yellow-400 w-4 h-4" />,
    SQLite: <SiSqlite className="text-blue-400 w-4 h-4" />,
    Python: <SiPython className="text-yellow-400 w-4 h-4" />,
    Flask: <SiFlask className="text-black w-4 h-4" />,
    JavaScript: <SiJavascript className="text-yellow-300 w-4 h-4" />,
    Git: <SiGit className="text-orange-500 w-4 h-4" />,
    GitHub: <SiGithub className="text-gray-200 w-4 h-4" />,
    Salesforce: <SiSalesforce className="text-blue-500 w-4 h-4" />,
  };

  // 🔹 Capsule with icon + label
  const Capsule = ({ label }) => (
    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm shadow-sm transition hover:shadow-lg hover:scale-[1.03]">
      {techIcons[label] && <span>{techIcons[label]}</span>}
      <span className="text-xs font-medium text-cyan-300">{label}</span>
    </div>
  );

const Services = () => {
  useEffect(() => {
    AOS.init({ duration: 800 });
  }, []);

  const services = [
    {
      title: "Web Development",
      description:
        "Designing and developing responsive, high-performance websites that look great on any device.",
      icon: "🌐",
      tooltip: "Full-stack Websites",
      stack: ["React", "Node.js", "Express", "MongoDB", "HTML5", "CSS3"],
      cta: "Let’s build your next website!",
    },
    {
      title: "App Development",
      description:
        "Building intuitive and scalable Android apps focused on smooth UX and real-world utility.",
      icon: "📱",
      tooltip: "Android Apps",
      stack: ["Java", "Firebase", "SQLite", "XML"],
      cta: "Your app idea deserves life!",
    },
    {
      title: "Automation Solutions",
      description:
        "Crafting intelligent AI/ML-powered automations for businesses and smart homes.",
      icon: "🤖",
      tooltip: "AI + ML Projects",
      stack: ["Python", "Flask", "Firebase", "OpenCV", "scikit-learn"],
      cta: "Let’s automate something awesome.",
    },
    {
      title: "API & CRM Integrations",
      description:
        "Developing robust sync pipelines, custom OAuth flows, secure webhook endpoints, and REST/SOAP API handlers to bridge Salesforce, HubSpot, and external systems.",
      icon: "🔄",
      tooltip: "Enterprise Tool Sync",
      stack: ["Salesforce", "Node.js", "Firebase", "Python", "JavaScript"],
      cta: "Connect your enterprise tools seamlessly.",
    },
    {
      title: "Salesforce Development",
      description:
        "Developing scalable business solutions using Salesforce automation, Lightning components, and CRM customization.",
      icon: "⚙️",
      tooltip: "CRM & Enterprise Tools",
      stack: ["Salesforce", "LWC", "Apex", "Flow", "Process Builder"],
      cta: "Let’s streamline your enterprise workflow.",
    },
  ];

  return (
    <section className="min-h-screen py-16 px-6 mt-6 text-white">
      <div className="max-w-7xl mx-auto">
        {/* Typing Animation Title */}
        <h2 className="text-4xl font-bold text-center mb-12">
          <Typewriter
            words={['My Services']}
            loop={1}
            typeSpeed={80}
            deleteSpeed={40}
            cursor
          />
        </h2>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className="bg-white/10 border border-white/20 backdrop-blur-lg rounded-xl shadow-lg p-6 text-center hover:-translate-y-2 hover:shadow-2xl transition duration-300 ease-in-out"
            >
              {/* Icon + Tooltip */}
              <div
                className="mx-auto mb-4 w-16 h-16 flex items-center justify-center rounded-full text-3xl bg-indigo-600 text-white shadow-md"
                data-tooltip-id={`tooltip-${index}`}
              >
                {service.icon}
              </div>
              <ReactTooltip
                id={`tooltip-${index}`}
                place="top"
                content={service.tooltip}
                className="!bg-gray-800 !text-white !text-sm !py-1 !px-3"
              />

              {/* Title & Description */}
              <h3 className="text-2xl font-semibold mb-2">{service.title}</h3>
              <p className="text-gray-300 text-sm mb-4">{service.description}</p>

              {/* Stack Chips */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {service.stack.map((tech, i) => (
                  <Capsule key={i} label={tech} />
                ))}
              </div>

              {/* CTA */}
              <p className="text-indigo-400 text-sm font-semibold italic">{service.cta}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
